import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  Buildings, Globe, ArrowsClockwise, ArrowRight, Sparkle,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * Region Leaderboard — top-level admin card showing per-region health
 * at a glance. Pulls `/api/admin/analytics/regions` which iterates the
 * `expansion_cities` collection and returns counts for trucks, owners,
 * customers, preloaded queue, and founding-pro promo progress.
 *
 * Window is selectable (7/30/90 days). Region rows link to the
 * dedicated /admin/preloaded-trucks page for deeper drill-in.
 */
const WINDOW_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

export default function RegionLeaderboardCard() {
  const [days, setDays] = useState(30);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (windowDays) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/analytics/regions?days=${windowDays}`);
      setRegions(data?.regions || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load region analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="region-leaderboard">
      <header className="flex items-end justify-between gap-3 px-5 py-4 border-b border-border flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
            <Buildings weight="duotone" className="h-3.5 w-3.5" />
            Expansion · region breakdown
          </div>
          <h2 className="font-display font-semibold text-xl tracking-tight">Per-region leaderboard</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            New trucks, owners, customers, and Founding-Pro progress for every market.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-2 text-xs font-medium"
            data-testid="region-window-select"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => load(days)}
            disabled={loading}
            data-testid="region-refresh-btn"
          >
            <ArrowsClockwise weight="bold" className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </header>

      {loading && regions.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading region data…</div>
      ) : regions.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-muted-foreground italic">No regions configured.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="px-4 py-2 font-semibold">Region</th>
                <th className="px-3 py-2 font-semibold text-right">New trucks</th>
                <th className="px-3 py-2 font-semibold text-right">Total trucks</th>
                <th className="px-3 py-2 font-semibold text-right">New owners</th>
                <th className="px-3 py-2 font-semibold text-right">New customers</th>
                <th className="px-3 py-2 font-semibold text-right">Preloaded queue</th>
                <th className="px-3 py-2 font-semibold text-right">Founding Pro</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {regions.map((r) => {
                const isLive = r.public_status === "live";
                return (
                  <tr key={r.region_slug} className="border-t border-border" data-testid={`region-row-${r.region_slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-foreground">{r.region_name}</span>
                        {r.state && <span className="text-muted-foreground text-xs">{r.state}</span>}
                        {isLive ? (
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                            <Globe weight="fill" className="h-2.5 w-2.5" /> Live
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            Coming soon
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="font-bold text-foreground">{r.trucks.new}</span>
                    </td>
                    <td className="px-3 py-3 text-right text-muted-foreground">
                      {r.trucks.total}{" "}
                      {r.trucks.published > 0 && r.trucks.published !== r.trucks.total && (
                        <span className="text-[10px]">({r.trucks.published} pub)</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="font-bold text-foreground">{r.owners.new}</span>{" "}
                      <span className="text-muted-foreground text-[10px]">of {r.owners.total}</span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="font-bold text-foreground">{r.customers.new}</span>{" "}
                      <span className="text-muted-foreground text-[10px]">of {r.customers.total}</span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      {r.preloaded.total > 0 ? (
                        <>
                          <span className="font-bold text-foreground">{r.preloaded.total}</span>
                          <div className="text-[10px] text-muted-foreground">
                            {r.preloaded.verified}v · {r.preloaded.pending}p
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {r.founding.total > 0 ? (
                        <span className="inline-flex items-center gap-1">
                          <Sparkle weight="fill" className={`h-3.5 w-3.5 ${r.founding.active ? "text-amber-600" : "text-muted-foreground"}`} />
                          <span className="font-bold">
                            {r.founding.granted}/{r.founding.total}
                          </span>
                          {r.founding.reserved > 0 && (
                            <span className="text-[10px] text-amber-700">+{r.founding.reserved}res</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Link
                        to={`/admin/preloaded-trucks?city=${r.region_slug}`}
                        className="inline-flex items-center gap-0.5 text-primary hover:underline text-xs"
                        data-testid={`region-drill-${r.region_slug}`}
                      >
                        Manage <ArrowRight weight="bold" className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
